import React, { useEffect, useRef, useState } from 'react';
import { useDocumentTitle } from '../hooks/use-document-title';
import { NavbarAndContentContainer } from '../layout/layout-container.styled';
import { Navbar } from '../layout/visitor-log/navbar';
import { listItemMotion } from '../../motion';
import { StyledButton } from '../styled-button/styled-button';
import { CheckRing } from '../icons/check-ring';
import { DefaultComponentProps } from '../app-router/component-props';
import { NavigateFunction, useLocation } from 'react-router-dom';
import { createItem } from '../requests/requests';
import { DailyLogIcon } from '../icons/daily-log';
import { VisitorLogIcon } from '../icons/visitor-log';
import { ScanIcon } from '../icons/scan';
import { ParcelIcon } from '../icons/parcel';
import { User } from '../../interfaces/user-interfaces';
import { useAuth } from '../../context/auth-provider';
import { can, PERMISSIONS } from '../../auth/permissions';
import { ActivityFeed } from '../activity/activity-feed';
import toast from '../../utils/toast';
import { useOptionAdvance } from '../hooks/use-option-advance';
import { useHandoffInbox } from '../iq/handoff-context';
import { ShiftClock } from '../shifts/shift-clock';
import { useShifts } from '../shifts/shift-context';
import { getEntitlement } from '../../api/plan-client';
import { AppButton } from '../button/app-button';
import { palette } from '../../style-utils/theme';
import { useExpectedToday } from '../hooks/use-expected-today';
import { useExpectedStays } from '../hooks/use-expected-stays';
import { expectedCardVisible } from './visitor-log/expected/expected-model';
import {
  expectedBadgeCount,
  expectedStaysCardVisible,
} from './visitor-log/expected/expected-stays-model';
import {
  MenuIcon,
  MenuItem,
  MenuOption,
  SEND_REPORT_OPTION,
  buildMenuItems,
  navigationTargets,
} from './main-menu-model';
import {
  BoardCell,
  BoardGrid,
  BoardSwitch,
  BodyCell,
  BuildingGateCard,
  BuildingGateGrid,
  BuildingGateMeta,
  BuildingGateName,
  ClockSlot,
  DeskArea,
  DeskGrid,
  FeedSlot,
  GateHead,
  GateStack,
  GateText,
  GateTitle,
  MenuBoard,
  MenuBoardFrame,
  PairAction,
  PairActions,
  PairCard,
  PairHead,
  PairLabel,
  TileSkeleton,
  TitleCell,
} from './main-menu.styled';

// THE WHITELIST IS DERIVED, NOT HAND-WRITTEN. This used to be a switch listing
// every destination, which meant a tile and its case could drift apart (and a
// tile hidden by a permission gate still had a live case). `targets` is built
// from the tiles that were actually rendered for THIS user, so a destination
// exists only while the tile that leads to it does.
const handleNavigation = (
  selectedOption: string,
  targets: Record<string, string>,
  navigate: NavigateFunction
) => {
  const to = targets[selectedOption];
  if (!to) {
    console.error('Unknown option:', selectedOption);

    return;
  }
  navigate(to, { replace: false });
};

const ICONS: Record<MenuIcon, React.ReactNode> = {
  visitor: <VisitorLogIcon />,
  daily: <DailyLogIcon />,
  check: <CheckRing />,
  scan: <ScanIcon color={palette.slate} />,
  // The parcel doors wore the VISITOR glyph — a person — so three different
  // errands carried the same face on the one screen whose point is that a tile
  // is recognised before it is read.
  parcel: <ParcelIcon />,
};

export const MainMenu: React.FC<DefaultComponentProps> = ({
  navigate,
  state,
  path,
  selectedBuilding,
  buildings = [],
  buildingsLoading = false,
  onSelectBuilding,
}) => {
  useDocumentTitle('Main menu');

  // The value used to feed this page's own Sidebar; the rail is DeskShell's
  // now. The setter stays: it is what useOptionAdvance marks the pressed
  // option with while the advance is in flight.
  const [, setSelectedOption] = useState('');
  const {
    user,
    permissions,
  }: { user: User | null | undefined; permissions: string[] } = useAuth();
  const { email, full_name } = user || {};
  // Every gate below mirrors the endpoint its tile calls, rather than a role
  // list that happens to line up today.
  const canSeeRoster = can(permissions, PERMISSIONS.OCCUPANCY_READ);
  const canSeePackages = can(permissions, PERMISSIONS.PACKAGE_READ);
  const canSignVisitorsOut = can(permissions, PERMISSIONS.VISITORLOG_CREATE);
  const canReadReport = can(permissions, PERMISSIONS.REPORT_READ);
  const canSendReport = can(permissions, PERMISSIONS.REPORT_EXPORT);
  // THE SHIFT CLOCK IS NOT AN AI SURFACE, so it is NOT behind IQ_USE and NOT
  // behind the org's IQ entitlement. The backend holds itself to the same rule:
  // every /api/shifts route must work with ai_config.enabled = false
  // (lobbero_be/src/CRUD/shifts.js:5-9).
  const canClockShift = can(permissions, PERMISSIONS.SHIFT_CLOCK);
  // Deliberately a DIFFERENT permission. /shifts/monitor is gated on user:read
  // server-side and 403s a concierge outright, so gating the tile on
  // SHIFT_CLOCK would hand the desk a button that cannot work.
  const canSeeShiftMonitor = can(permissions, PERMISSIONS.USER_READ);
  const clockBuildingId = selectedBuilding ? Number(selectedBuilding.id) : NaN;
  const { setBuilding: setHandoffBuilding } = useHandoffInbox();
  // THE HANDOVER IS PER DESK, and this stays even though the menu no longer has
  // a Handover tile: the shared inbox is what the IQ panel and the clock card
  // both read, and unscoped it shows the newest handover anywhere this person
  // can see — for a property manager covering four buildings, usually not the
  // one named on the screen they are looking at.
  //
  // THE TILE IS GONE ON PURPOSE (owner, 2026-08-13: "we clock out and
  // handover"). Nothing is stranded by that: ShiftClock raises the receipt on
  // clock-out and claims the incoming one on clock-in, and it owns its own
  // <HandoffModal> for both. This screen no longer needs one.
  useEffect(() => {
    setHandoffBuilding(
      Number.isInteger(clockBuildingId) ? clockBuildingId : null
    );
  }, [clockBuildingId, setHandoffBuilding]);
  const hasBuildingContext = Boolean(selectedBuilding) && !buildingsLoading;
  const [loading, setLoading] = useState(false);
  const reportRequestRef = useRef(false);

  // ── THE EXPECTED CARD (resident-portal Plan 2, RP-2.11) ───────────────────
  // Shown to whoever may read passes (the permission GET /expected checks) at a
  // building that accepts them. The building list is cached for the session,
  // so the LIVE /expected answer also gets a say: the moment it reports the
  // switch off, the card goes. The same read gives the badge — visitors
  // signable now whom nobody has signed in yet.
  const canReadPasses = can(permissions, PERMISSIONS.VISITORPASS_READ);
  const buildingAcceptsPasses = selectedBuilding?.visitor_passes_enabled === true;
  const expectedToday = useExpectedToday(
    Number.isInteger(clockBuildingId) ? clockBuildingId : null,
    { enabled: hasBuildingContext && canReadPasses && buildingAcceptsPasses }
  );
  const canSeeExpected = expectedCardVisible({
    canReadPasses,
    buildingAcceptsPasses,
    livePassesEnabled: expectedToday.passesEnabled,
  });
  // THE SHORT-STAY SHARE OF THE SAME CARD (Lobbero Stays plan S3). stays:desk
  // at a building whose row says Stays is on; the live lane withdraws it the
  // moment it answers off. Passes off does NOT hide the card while Stays is on.
  const canDeskStays = can(permissions, PERMISSIONS.STAYS_DESK);
  // IS7: see expected-page.tsx. The lane's own answer withdraws the card if it is empty.
  const buildingHasStays = selectedBuilding?.stays_enabled === true || user?.stays_building_link === true;
  const expectedStays = useExpectedStays(
    Number.isInteger(clockBuildingId) ? clockBuildingId : null,
    { enabled: hasBuildingContext && canDeskStays && buildingHasStays }
  );
  const canSeeExpectedStays = expectedStaysCardVisible({
    canDeskStays,
    buildingStaysEnabled: buildingHasStays,
    liveStaysEnabled: expectedStays.staysEnabled,
  });

  // THE MENU ITSELF — built from the permissions above, in main-menu-model.ts.
  // No groups and no headings; three of the cards carry In and Out inside them
  // so the desk names the subject once instead of reading ten look-alike rows.
  const allItems = buildMenuItems(
    {
      canSeePackages,
      canSignVisitorsOut,
      canSeeRoster,
      canSeeShiftMonitor,
      canSendReport,
      canSeeExpected,
      canSeeExpectedStays,
    },
    {
      expected: expectedBadgeCount(
        canSeeExpected ? expectedToday.rows : null,
        canSeeExpectedStays ? expectedStays.counts : null
      ),
    }
  );

  // ── YOU CLOCK IN FIRST ────────────────────────────────────────────────────
  // Read from the shared shift state, the same one the clock card renders from
  // (shift-context.tsx) — so the card and these buttons cannot disagree, and
  // neither of them owns the question. null = the first read has not answered,
  // and that is NOT the same as clocked out: treating it as such would light
  // every button up and then take them away.
  const { isOnShiftAt } = useShifts();
  const onShift = Number.isInteger(clockBuildingId)
    ? isOnShiftAt(clockBuildingId)
    : null;
  // Only people who HAVE a clock can be asked to use it. An owner who never
  // clocks in anywhere would otherwise find the whole menu closed to them.
  const clockGateApplies = canClockShift && Number.isInteger(clockBuildingId);
  const workBlocked = clockGateApplies && onShift !== true;
  // ── OFF THE CLOCK, THE CLOCK IS THE SCREEN ────────────────────────────────
  // Not "the work tiles are hidden and the read-only ones stay": NOTHING is
  // rendered but the clock. Two earlier shapes of this gate are described in
  // main-menu.styled.tsx (GateScreen) and both failed the same way — they left
  // a menu on screen and then explained, in chrome beside it, that most of it
  // did not work.
  //
  // STRICTLY `=== false`, not `!== true`. While the first read is in flight
  // (null) the menu stays rendered with its work tiles merely disabled —
  // replacing the whole screen on an unanswered question and putting it back a
  // moment later would move the grid under a thumb that is already coming down.
  // ── PICK A BUILDING BEFORE ANYTHING ELSE (WP-07 / BUG-015, ruling R-5) ────
  // A multi-building user chooses EXPLICITLY. There is no silent default, which
  // is what R-5 settled: the shell used to take whichever building the API
  // happened to return first, and until WP-06 that was the LAST one
  // alphabetically for a manager.
  //
  // GATED ON THE RENDERED `buildings` ARRAY, never on role and never on
  // `user.property_ids`. That field used to be user_properties-only and came
  // back empty for managers covered by a grant (the server now builds it from
  // access_scope in src/auth/session-user.js), but the rendered list is what
  // the person can actually open, so it stays the gate.
  //
  // AFTER the loading branch, not before: `buildingsLoading` true with an empty
  // array is "not answered yet", and rendering a picker over it would tell a
  // manager they have no buildings for as long as the request takes.
  //
  // Ahead of the clock gate deliberately. Clocking in happens AT a building, so
  // being asked to clock in before saying which building is the wrong order —
  // and ClockSlot would otherwise be handed a NaN buildingId.
  /* WHERE THEY WERE ACTUALLY GOING.
     app-router redirects any building-bound desk address here when no building
     has been chosen yet, and it now sends the address along. Naming it turns a
     silent bounce into an answerable question, and picking a building carries
     them back to it. */
  const location = useLocation();
  const deskState = location.state as {
    deskFrom?: string;
    deskSearch?: string;
  } | null;
  const deskFrom = deskState?.deskFrom || '';
  // The address's query string (?pass=<id> from the walk-in banner), put back
  // on the way through. Only a real query string: anything else is dropped.
  const deskSearch =
    deskState?.deskSearch && deskState.deskSearch.startsWith('?')
      ? deskState.deskSearch
      : '';
  const DESK_LABELS: Record<string, string> = {
    '/parcels': 'The parcel desk',
    '/roster': 'The roster',
    '/view-report': 'The daily report',
    '/visitor-exit': 'Signing a visitor out',
    '/daily-log-exit': 'Closing an activity',
    '/iq-handover': 'The handover',
    '/visitor-log': 'The visitor log',
    '/daily-log': 'Building activities',
    '/visitor-log/resident': 'Resident in / out',
    '/visitor-log/expected': 'The expected list',
    '/visitor-log/passes': 'The visitor passes',
  };
  const deskDestinationLabel = deskFrom
    ? DESK_LABELS[deskFrom] ||
      (deskFrom.startsWith('/visitor-log')
        ? 'The visitor log'
        : deskFrom.startsWith('/daily-log')
          ? 'Building activities'
          : '')
    : '';

  const mustPickBuilding =
    !buildingsLoading && buildings.length > 1 && !selectedBuilding;

  /* BACK TO WHAT THEY ASKED FOR — A FRAME AFTER THE PICK, NOT IN IT. The pick
     redraws this menu (the gate gives way to the clock and the tiles), and
     leaving the route in that same commit told the just-mounted fills to exit
     in the tick after they mounted: framer-motion 13.2 never finishes such an
     exit (see motion/state-switch.tsx), the desk's content layer waits on it in
     "wait" mode, and the address read /visitor-log/expected while the screen
     stayed on the Main Menu until a reload (QA 2026-10-22). So the pick only
     marks the return; it is taken once the building is on screen, two frames
     later. */
  const [returnPending, setReturnPending] = useState(false);
  useEffect(() => {
    if (!returnPending || !deskFrom || !selectedBuilding) {
      return undefined;
    }
    let second = 0;
    const first = window.requestAnimationFrame(() => {
      second = window.requestAnimationFrame(() => {
        setReturnPending(false);
        navigate(deskFrom + deskSearch, { replace: true });
      });
    });

    return () => {
      window.cancelAnimationFrame(first);
      window.cancelAnimationFrame(second);
    };
  }, [returnPending, deskFrom, deskSearch, selectedBuilding, navigate]);

  // ── THE DESK CLOSES WHEN THE SUBSCRIPTION ENDS ──────────────────────────
  // Owner instruction, 2026-09-02: "the main menu must also be frozen and
  // there should be no in and out of any kind." The server refuses every desk
  // write with 402 SUBSCRIPTION_ENDED the moment no live subscription
  // remains; this is the screen saying so BEFORE somebody walks a wizard to a
  // refused save. STRICTLY === false for the FROZEN panel. While the read is
  // in flight (null) the board shows the tiles' silhouettes ('checking' in
  // boardState below) rather than live tiles that a 402 would then take away
  // from under a thumb already coming down. The read fails open on the client
  // (plan-client.ts catches to active: true), so 'checking' cannot stick.
  const [deskOpen, setDeskOpen] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    getEntitlement().then(r => {
      if (alive) setDeskOpen(r.active);
    });

    return () => {
      alive = false;
    };
  }, []);
  const deskFrozen = deskOpen === false;
  const canManageBilling = can(permissions, PERMISSIONS.BILLING_MANAGE);

  const gateShown =
    !mustPickBuilding && clockGateApplies && onShift === false;
  const items = allItems;
  // A SUBJECT carries In and Out; a BOARD is one button you read.
  // Split from what was actually BUILT for this user rather than by naming
  // ids here, so a card the permission gates reduced to one option lands in
  // the right grid on its own.
  const subjects = items.filter(i => i.options.length > 1);
  const boards = items.filter(i => i.options.length === 1);
  // Derived from what is ACTUALLY RENDERED (main-menu-model.ts rule 2), so a
  // tile that is not on the screen has no destination — the gate is not just
  // paint over a live route.
  const targets = navigationTargets(items);

  const { pendingValue, selectAndAdvance } = useOptionAdvance({
    onSelect: setSelectedOption,
    onAdvance: value => handleNavigation(value, targets, navigate),
  });

  const sendDailyReport = async () => {
    if (reportRequestRef.current || !selectedBuilding) {
      return;
    }

    reportRequestRef.current = true;
    try {
      setLoading(true);

      // No zone is sent: a daily report is the BUILDING's desk day on the
      // building's clock, whoever sends it (the server no longer reads one).
      await createItem('daily-report', {
        email,
        full_name,
        property_ids: [Number(selectedBuilding.id)],
      });

      toast.success('Report sent');
    } catch (error) {
      console.error('Error sending report:', error);
      toast.error('Error sending report.');
    } finally {
      reportRequestRef.current = false;
      setLoading(false);
    }
  };

  // Pressing anything on the menu: navigate, or run the one in-page action.
  //
  // "Send the report" SENDS THE REPORT. It used to select a tile that changed
  // nothing visible and enable a button at the foot of the page — the control
  // the desk got wrong most often, because the thing that changed was off the
  // bottom of a phone.
  // "Send the report" is now the ONLY action on this menu; everything else
  // navigates. It SENDS THE REPORT — it used to select a tile that changed
  // nothing visible and enable a button at the foot of the page, the control the
  // desk got wrong most often because the thing that changed was off the bottom
  // of a phone.
  const press = (option: MenuOption) => {
    if (option.kind === 'action') {
      setSelectedOption(option.id);
      if (option.id === SEND_REPORT_OPTION) sendDailyReport();

      return;
    }
    selectAndAdvance(option.id);
  };

  const isDisabled = (item: MenuItem, option: MenuOption): boolean =>
    (item.requiresBuilding && !hasBuildingContext) ||
    (option.id === SEND_REPORT_OPTION && loading) ||
    // Recording what happened on a shift needs a shift. Reading does not.
    (!!item.recordsWork && workBlocked);

  const showClock = canClockShift && Number.isInteger(clockBuildingId);

  // ── WHAT THE BOARD SHOWS, AS ONE WORD ─────────────────────────────────────
  // One state string keys the crossfade in BoardSwitch below, so the tiles,
  // the picker, the gate and the frozen notice replace each other inside the
  // same box instead of snapping. The precedence is the one the notes above
  // argue for: frozen beats everything; nothing live is drawn until the
  // buildings and the entitlement have answered; a building is chosen before
  // a shift is asked for; the gate comes before the menu.
  const boardState: 'frozen' | 'checking' | 'pick' | 'gate' | 'menu' =
    deskFrozen
      ? 'frozen'
      : deskOpen === null || buildingsLoading
        ? 'checking'
        : mustPickBuilding
          ? 'pick'
          : gateShown
            ? 'gate'
            : 'menu';
  // The LAYOUT follows the state: title column beside the tiles for the menu
  // (and its silhouette), one centred column for everything that replaces it.
  const boardMode: 'menu' | 'gate' =
    boardState === 'menu' || boardState === 'checking' ? 'menu' : 'gate';
  // ONE CLOCK, ONE CELL. The card is rendered at the same position of the
  // tree in every state that has one, and only ClockSlot's styles change
  // around it — see the note on ClockSlot in main-menu.styled.tsx for why a
  // remount here is not cosmetic. Frozen has no shift to start; without a
  // building there is no clock at all (showClock).
  const clockMounted = showClock && !deskFrozen;

  return (
    <NavbarAndContentContainer>
      <MenuBoardFrame>
        <MenuBoard $mode={boardMode}>
          {/* THE TITLE COLUMN GOES WITH THE MENU. "Main Menu — please select
              one of the options below" is a false sentence on the gate: there
              are no options, and the column it lives in is 40% of the row, so
              the one control on the screen was pushed into the right-hand
              third with a heading beside it promising choices that are not
              there. */}
          {boardMode === 'menu' ? (
            <TitleCell key="title">
              <Navbar
                path={path}
                navigation={[]}
                state={state}
                isLog={false}
                navigate={navigate}
                hasContent={false}
                currentPage={path}
              />
            </TitleCell>
          ) : null}
          {/* ON SHIFT, THE CARD SITS HERE — under "Main Menu", on the left.
              Owner instruction, 2026-08-11. It is a status, not an errand: in
              the grid it competed with the ten controls somebody is trying to
              pick from and pushed the first row of tiles down the page. OFF
              SHIFT the same card, in the same slot, is the whole screen. */}
          {clockMounted ? (
            <ClockSlot key="clock" $gate={boardMode === 'gate'}>
              <ShiftClock
                buildingId={clockBuildingId}
                buildingName={selectedBuilding?.property_name}
              />
            </ClockSlot>
          ) : null}
          <BodyCell key="body" $mode={boardMode}>
            {/* The floor is reserved for the menu shape only: on a gate the
                heading sits directly above the clock and a 320px floor would
                push the one control on the screen down by a screen. */}
            <BoardSwitch
              state={boardState}
              minHeight={boardMode === 'menu' ? 320 : undefined}
            >
              {boardState === 'frozen' ? (
                /* FROZEN BEATS EVERYTHING: with the subscription over there is
                   no building to pick, no shift to start and no errand to run —
                   one panel says so, with the one useful door (billing) for the
                   people who hold the key. role="status", like the other
                   gates. */
                <GateStack>
                  <GateHead $gate role="status">
                    <GateTitle>The desk is closed</GateTitle>
                    <GateText>
                      This organisation&apos;s subscription has ended, so
                      nothing can be logged in or out — no visitors, no
                      parcels, no building activities. Everything already
                      recorded is kept, and the desk reopens the moment the
                      subscription restarts.
                    </GateText>
                    {canManageBilling ? (
                      <AppButton onClick={() => navigate('/dashboard/billing')}>
                        Go to billing
                      </AppButton>
                    ) : (
                      <GateText>
                        Ask an account admin to renew the subscription.
                      </GateText>
                    )}
                  </GateHead>
                </GateStack>
              ) : boardState === 'checking' ? (
                /* THE SHAPE OF WHAT IS COMING, while the buildings and the
                   entitlement are read: the same grid, four silhouettes. */
                <DeskArea
                  role="status"
                  aria-busy="true"
                  aria-label="Loading the desk"
                >
                  <DeskGrid>
                    {[0, 1, 2, 3].map(slot => (
                      <TileSkeleton key={slot} aria-hidden="true" />
                    ))}
                  </DeskGrid>
                </DeskArea>
              ) : boardState === 'pick' ? (
                <GateStack>
                  <GateHead $gate role="status">
                    <GateTitle>Choose a building</GateTitle>
                    <GateText>
                      {deskDestinationLabel
                        ? `${deskDestinationLabel} is recorded against one building, and you cover ${buildings.length}. Pick the one you are working from and we will take you straight there.`
                        : `You cover ${buildings.length} buildings. Everything on the desk — visitors, parcels, activities — is recorded against one of them, so pick the one you are working from.`}
                    </GateText>
                  </GateHead>
                  <BuildingGateGrid>
                    {buildings.map(building => (
                      <BuildingGateCard
                        key={building.id}
                        type="button"
                        onClick={() => {
                          if (onSelectBuilding) {
                            onSelectBuilding(String(building.id));
                          }
                          /* BACK TO WHAT THEY ASKED FOR. Answering the question
                             the gate asked should not also cost them the page
                             they were opening — taken by the effect above,
                             a frame after this commit. */
                          if (deskFrom) {
                            setReturnPending(true);
                          }
                        }}
                      >
                        <BuildingGateName>{building.property_name}</BuildingGateName>
                        {building.portfolio_name ? (
                          <BuildingGateMeta>
                            {building.portfolio_name}
                          </BuildingGateMeta>
                        ) : null}
                      </BuildingGateCard>
                    ))}
                  </BuildingGateGrid>
                </GateStack>
              ) : boardState === 'gate' ? (
                /* OFF THE CLOCK, THE GATE — the whole content area, one control
                   on it (the clock in the cell below this one), and the title
                   column is not rendered at all. role="status" so a screen
                   reader is told why the menu it was on has been replaced,
                   rather than meeting a changed page in silence. */
                <GateStack>
                  <GateHead $gate role="status">
                    <GateTitle>Start your shift</GateTitle>
                    <GateText>
                      {selectedBuilding?.property_name
                        ? `Clock in at ${selectedBuilding.property_name} to open the desk. Visitors, residents, parcels and building activities are all recorded against your shift.`
                        : 'Clock in to open the desk. Visitors, residents, parcels and building activities are all recorded against your shift.'}
                    </GateText>
                  </GateHead>
                </GateStack>
              ) : (
                <>
                  {/* TWO GRIDS, TWO KINDS OF THING. The subjects are what you
                      RECORD; the boards are what you READ. They were one list of
                      six, which made a status board look like a sixth errand.

                      DeskArea is the containment context both grids measure
                      themselves against — see its note: this column sits beside
                      the title column, so a viewport breakpoint is measuring the
                      wrong box.

                      The tiles ARRIVE IN ORDER (listItemMotion, capped cascade)
                      and never with layout animation: a grid of at most six
                      fixed cells has nothing to reorder. */}
                  <DeskArea>
                    <DeskGrid>
                      {subjects.map((item, index) => (
                        // ONE SUBJECT, TWO DIRECTIONS. Named once, with In and
                        // Out inside it.
                        <PairCard
                          key={item.id}
                          {...listItemMotion(index)}
                          layout={false}
                        >
                          <PairHead>
                            {ICONS[item.icon]}
                            <PairLabel>{item.label}</PairLabel>
                          </PairHead>
                          <PairActions>
                            {item.options.map(option => (
                              <PairAction
                                key={option.id}
                                type="button"
                                onClick={() => press(option)}
                                disabled={isDisabled(item, option)}
                                aria-label={`${item.label} ${option.label}`}
                              >
                                {option.label}
                              </PairAction>
                            ))}
                          </PairActions>
                        </PairCard>
                      ))}
                    </DeskGrid>

                    {boards.length > 0 && (
                      <BoardGrid>
                        {boards.map((item, index) => (
                          <BoardCell
                            key={item.id}
                            {...listItemMotion(subjects.length + index)}
                            layout={false}
                          >
                            <StyledButton
                              // The icon renders on a main-menu path. There is no
                              // selected-check in this menu because there is
                              // nothing to select — everything acts.
                              path={path}
                              onClick={() => press(item.options[0])}
                              text={item.label}
                              icon={ICONS[item.icon]}
                              isSelected={false}
                              pending={pendingValue === item.options[0].id}
                              disabled={isDisabled(item, item.options[0])}
                              // A count on the card (the Expected card's
                              // signable-now visitors). The badge is
                              // aria-hidden; its number is read in the name.
                              badge={item.badge}
                              ariaLabel={item.accessibleName}
                            />
                          </BoardCell>
                        ))}
                      </BoardGrid>
                    )}
                  </DeskArea>

                  {/* THE MINI ACTIVITY DASHBOARD (owner brief §9, decision Q-10).
                      A glance at what has happened at THIS desk, grouped the way
                      the desk thinks about it — residents, visitors, parcels,
                      daily log — with who did it and when. Compact on purpose:
                      this sits under the menu and must not become a second admin
                      screen, which the brief asks for explicitly. It renders only
                      with a building in context, because a desk feed with no
                      desk is meaningless.

                      AND ONLY ON SHIFT. Owner instruction, 2026-08-14: a
                      concierge clocks in FIRST, then sees. This panel used to
                      render on the gate too, under a "Before you start" heading
                      — the incoming shift reading what happened before they
                      arrived. That is a reasonable screen and it is not the
                      rule: the gate is the one place the product says nothing is
                      available yet, and a full activity feed sitting under that
                      sentence contradicts it. Off the clock there is one thing
                      on this screen and it is the clock.

                      The menu fill is only reached when no gate is up, and
                      gateShown is false for anybody who has no clock at all (see
                      clockGateApplies), so an owner or admin who never clocks in
                      anywhere still gets the feed. */}
                  {hasBuildingContext && canReadReport && (
                    <FeedSlot>
                      <ActivityFeed
                        buildingId={Number(selectedBuilding?.id)}
                        showSummary
                        title={`${selectedBuilding?.property_name || 'This building'} — everything in and out`}
                      />
                    </FeedSlot>
                  )}
                </>
              )}
            </BoardSwitch>
          </BodyCell>
        </MenuBoard>
      </MenuBoardFrame>
    </NavbarAndContentContainer>
  );
};
